/**
 * Session list for the new stack — a clean rewrite of the legacy
 * apps/project/components/SessionList.tsx (468 lines), which carried the whole
 * RFC 0147/0113 branch layer (BranchListEntry, integration branches, inclusion
 * sets, ~10 branch callbacks) that the RFC 0246 model deleted. Sessions are
 * branch-less now, so none of that exists here.
 *
 * Kept (the good UX): grouped rows, collapsible headers with counts, a create
 * affordance, per-row rename/delete.
 *
 * The groups are RFC 0286's, and they group by whether YOU have to act. The
 * previous labels (RUNNING / NEEDS REVIEW / MERGED) came from the legacy branch
 * list and described a review workflow sessions do not have: review belongs to a
 * pull request, not a conversation. They also had no field behind them, so
 * `status ?? 'active'` put every session in RUNNING and the other two rendered
 * empty forever.
 *
 * "Waiting on you" leads, because a session stopped on a question is the only
 * one costing someone time right now, and Working reads as "the machine has
 * this, leave it alone" — the opposite of what an unanswered question means.
 * Self-contained: the status arrives on the row (see sessionStatus).
 */

import { isSessionDone } from '@mod/core/model'
import { ChevronDown, ChevronRight, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { ProjectChoice } from '../../services/sessions/projectChoices'
import { SessionRow, type SessionRowData } from './SessionRow'

type Group = 'asking' | 'idle' | 'working' | 'done'

/** Act, decide, ignore, forget. */
const GROUP_ORDER: Group[] = ['asking', 'idle', 'working', 'done']
const GROUP_LABELS: Record<Group, string> = {
  asking: 'Waiting on you',
  idle: 'Idle',
  working: 'Working',
  done: 'Done',
}
/** Done is filed-and-forgotten by definition, so it opens closed. */
const COLLAPSED_BY_DEFAULT: Partial<Record<Group, boolean>> = { done: true }

export interface SessionListProps {
  sessions: SessionRowData[]
  activeSessionId: string | null
  isLoading?: boolean
  /**
   * Where a new session may land (RFC 0290 S5). Absent, or one entry, means
   * there is no choice to make and `+` creates directly: a menu of one is a
   * click asking permission to do the only thing it can.
   */
  projects?: ProjectChoice[]
  /**
   * Why creating is refused right now, when it is. Set when there is no choice
   * to offer and the ONE project this list would create in cannot be answered.
   *
   * A control that silently does nothing is worse than a disabled one: the
   * second gate in `resolveCreateTarget` has to refuse, so the button has to
   * SAY it will. Absent means creating is fine.
   */
  createDisabledReason?: string
  onCreate: (projectId?: string) => void
  onOpen: (sessionId: string) => void
  onRename?: (sessionId: string, title: string) => void
  onDelete?: (sessionId: string) => void
  /** File as done, or un-file. Absent leaves the row menu without the action. */
  onComplete?: (sessionId: string, done: boolean) => void
}

export function SessionList({
  sessions,
  activeSessionId,
  isLoading = false,
  projects,
  createDisabledReason,
  onCreate,
  onOpen,
  onRename,
  onDelete,
  onComplete,
}: SessionListProps) {
  const [collapsed, setCollapsed] =
    useState<Partial<Record<Group, boolean>>>(COLLAPSED_BY_DEFAULT)
  const [picking, setPicking] = useState(false)
  const choices = projects ?? []
  const pickable = choices.length > 1
  // Only when there is nothing to pick: with a menu, each row carries its own
  // reason and the button's job is to open it.
  const blocked = !pickable && createDisabledReason

  /**
   * The sessions an AGENT opened, which the list does not lead with.
   *
   * A turn that fans work out opens six sessions in one breath, and at the top
   * level those six outnumber and outrank the one conversation a person
   * actually started — the list sorts by recency, so the machine's own
   * bookkeeping always wins. The thread that opened them draws each as a card
   * (see threadCards), which is where somebody would look: "what did that turn
   * do" is a question about the turn.
   *
   * TWO EXCEPTIONS, and they are the same rule — never hide something that
   * needs a person.
   *
   *  - A sub-session STOPPED ON A QUESTION climbs back out. It is the one state
   *    where nothing happens until somebody answers, and burying it inside a
   *    thread nobody is reading is how a session waits forever.
   *  - A sub-session whose parent is not in this list stays put. The parent may
   *    be deleted, or may live in a project this list cannot see, and hiding a
   *    row whose only other door does not exist here makes it unreachable
   *    rather than tidy.
   *
   * Counted and revealable either way: a filter with no way to see what it
   * removed is indistinguishable from sessions that vanished.
   */
  const ids = useMemo(() => new Set(sessions.map((s) => s.id)), [sessions])
  const nested = useMemo(
    () =>
      sessions.filter(
        (s) =>
          s.parentSessionId != null &&
          ids.has(s.parentSessionId) &&
          s.status !== 'asking',
      ),
    [sessions, ids],
  )
  const [showNested, setShowNested] = useState(false)
  const listed = useMemo(() => {
    if (showNested || nested.length === 0) return sessions
    const hidden = new Set(nested.map((s) => s.id))
    return sessions.filter((s) => !hidden.has(s.id))
  }, [sessions, nested, showNested])

  const groups = useMemo(() => {
    const byGroup: Record<Group, SessionRowData[]> = {
      asking: [],
      idle: [],
      working: [],
      done: [],
    }
    for (const session of listed) {
      // Done is a READ rule (isSessionDone): filed, and nothing since. Filing
      // writes only completedAt and nothing clears it, so a session that starts
      // working again must leave Done by itself, not sit there while it runs.
      if (isSessionDone(session)) byGroup.done.push(session)
      // A session whose doc was not read has NO status, and lands in Idle rather
      // than inventing one. It is the honest place: unread and quiet look the
      // same from here, and only Waiting-on-you must never be guessed at.
      else if (session.status === 'asking') byGroup.asking.push(session)
      else if (session.status === 'running' || session.status === 'queued')
        byGroup.working.push(session)
      else byGroup.idle.push(session)
    }
    byGroup.asking.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
    byGroup.working.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
    byGroup.idle.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
    // Done sorts by when it was FILED, not last activity: the question there is
    // "what did I just finish", which updatedAt cannot answer.
    byGroup.done.sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0))
    return byGroup
  }, [listed])

  // What the EMPTY state is about: whether anything is listed, not whether the
  // registry has rows. A project whose only sessions were opened by an agent
  // shows the toggle below rather than "No sessions yet", which would be a lie
  // about a filter this component applied itself.
  const total = listed.length

  return (
    <div className="flex flex-col gap-1 py-2">
      <div className="flex items-center justify-between px-3 pb-1">
        <span className="flex items-center gap-2">
          <span className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
            Sessions
          </span>
          {/* The one number worth a permanent place: everything else in this
              list can wait, and a session stopped on a question cannot. */}
          {groups.asking.length > 0 && (
            <span className="rounded-full bg-yellow-500/15 px-1.5 py-0.5 font-medium text-[10px] text-yellow-600 dark:text-yellow-400">
              {groups.asking.length} waiting on you
            </span>
          )}
        </span>
        <div className="relative">
          <button
            type="button"
            onClick={() => (pickable ? setPicking((p) => !p) : onCreate())}
            disabled={Boolean(blocked)}
            title={blocked || (pickable ? 'New session in…' : 'New session')}
            aria-haspopup={pickable ? 'menu' : undefined}
            aria-expanded={pickable ? picking : undefined}
            className={`rounded p-0.5 text-muted-foreground transition-colors ${
              blocked
                ? 'cursor-not-allowed opacity-40'
                : 'hover:bg-muted hover:text-foreground'
            }`}
          >
            <Plus size={14} />
          </button>
          {pickable && picking && (
            <div
              role="menu"
              className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-border bg-popover py-1 shadow-md"
            >
              {choices.map((choice) => (
                <button
                  key={choice.projectId}
                  type="button"
                  role="menuitem"
                  disabled={choice.disabled}
                  title={choice.reason}
                  onClick={() => {
                    // Belt and braces. `disabled` already stops a mouse, and
                    // this stops everything else: the list is a render old by
                    // the time it is clicked, and the cost of accepting one
                    // unservable choice is a session nobody can answer.
                    if (choice.disabled) return
                    setPicking(false)
                    onCreate(choice.projectId)
                  }}
                  className={`flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left ${
                    choice.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-muted'
                  }`}
                >
                  <span className="w-full truncate text-sm">{choice.name}</span>
                  {/* Shown, not hidden. An unavailable project a person cannot
                      see reads as one that has vanished. */}
                  <span className="w-full text-[10px] text-muted-foreground leading-snug">
                    {choice.reason}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {total === 0 ? (
        <div className="px-3 py-3 text-center text-muted-foreground text-xs">
          {isLoading ? (
            'Loading sessions…'
          ) : blocked ? (
            // The empty state is where a person is MOST likely to click, so it
            // gets the whole reason rather than a tooltip on a dead link.
            <>No sessions yet. {createDisabledReason}</>
          ) : (
            <>
              No sessions yet.{' '}
              <button
                type="button"
                onClick={() => onCreate()}
                className="text-blue-400 hover:underline"
              >
                Create one
              </button>
            </>
          )}
        </div>
      ) : (
        GROUP_ORDER.map((group) => {
          const rows = groups[group]
          if (rows.length === 0) return null
          const isCollapsed = collapsed[group] ?? false
          return (
            <div key={group} className="px-1">
              <button
                type="button"
                onClick={() =>
                  setCollapsed((c) => ({ ...c, [group]: !isCollapsed }))
                }
                className="group flex w-full items-center justify-between px-2 py-1"
              >
                <span className="flex items-center gap-1">
                  {isCollapsed ? (
                    <ChevronRight size={12} className="text-muted-foreground" />
                  ) : (
                    <ChevronDown size={12} className="text-muted-foreground" />
                  )}
                  <span className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide transition-colors group-hover:text-foreground">
                    {GROUP_LABELS[group]}
                  </span>
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {rows.length}
                </span>
              </button>
              {!isCollapsed && (
                <div className="flex flex-col gap-px">
                  {rows.map((session) => (
                    <SessionRow
                      key={session.id}
                      session={session}
                      isActive={session.id === activeSessionId}
                      onOpen={() => onOpen(session.id)}
                      onRename={onRename}
                      onDelete={onDelete}
                      onComplete={onComplete}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })
      )}

      {/* The escape hatch, and the disclosure that the list is filtered at all.
          Last, and quiet: it is the answer to "where did that session go",
          which is a question asked rarely and answered once. */}
      {nested.length > 0 && (
        <button
          type="button"
          onClick={() => setShowNested((s) => !s)}
          className="px-3 py-1.5 text-left text-[11px] text-muted-foreground hover:text-foreground"
        >
          {showNested
            ? 'Hide sessions opened by agents'
            : `${nested.length} opened by agents`}
        </button>
      )}
    </div>
  )
}
